import {
  BrowserCacheLocation,
  InteractionType,
  IPublicClientApplication,
  LogLevel,
  PublicClientApplication,
} from '@azure/msal-browser';
import { MsalGuardConfiguration } from '@azure/msal-angular';

/**
 * Microsoft Entra ID (Azure AD) — single-tenant (Vizipp org only).
 *
 * These two values are NOT secrets: the client id ships inside the browser
 * bundle by design, and the tenant id is a public identifier. Paste yours in
 * below (or leave the zero-GUID placeholders until you go live).
 *
 * The backend has a matching pair in `src/config.js` — keep them in sync.
 */
export const AZURE = {
  tenantId: 'af9ca679-0664-42be-baa2-f2962a8122b2',
  clientId: '6d75409d-fa23-4b26-a02a-a73a56053840',
};

const PLACEHOLDER = '00000000-0000-0000-0000-000000000000';

/** True once real ids are in place. */
export const AUTH_CONFIGURED =
  AZURE.tenantId !== PLACEHOLDER && AZURE.clientId !== PLACEHOLDER;

/**
 * Scopes requested at sign-in. We use Microsoft Graph's `User.Read` — a scope
 * users can consent to that needs no "Expose an API" setup — purely so MSAL
 * mints tokens. The backend is then authorized using the **ID token**, whose
 * audience is this app's client id (which the backend already accepts). This
 * keeps the whole thing to one app registration with no extra portal config.
 */
export const LOGIN_SCOPES = ['User.Read'];

export function msalInstanceFactory(): IPublicClientApplication {
  return new PublicClientApplication({
    auth: {
      clientId: AZURE.clientId,
      // Single-tenant authority — only accounts in this directory may sign in.
      authority: `https://login.microsoftonline.com/${AZURE.tenantId}`,
      redirectUri: '/',
      postLogoutRedirectUri: '/',
    },
    cache: {
      // LocalStorage keeps the session across tabs and reloads.
      cacheLocation: BrowserCacheLocation.LocalStorage,
    },
    system: {
      loggerOptions: {
        logLevel: LogLevel.Error,
        piiLoggingEnabled: false,
      },
    },
  });
}

export function msalGuardConfigFactory(): MsalGuardConfiguration {
  return {
    interactionType: InteractionType.Redirect,
    // Consent at login, so tokens are available silently afterwards.
    authRequest: { scopes: LOGIN_SCOPES },
  };
}
